/**
 * Authentication service
 */

import { apiClient } from './api';
import { LoginRequest, RegisterRequest, Token, User } from '../types';

export class AuthService {
  async login(credentials: LoginRequest): Promise<Token> {
    // Prefer JSON login endpoint; fallback to form-based if server returns error
    try {
      const token = await apiClient.post<Token>('/auth/login/json', credentials);
      apiClient.setAuthToken(token.access_token);
      return token;
    } catch (err: any) {
      console.warn('JSON login failed, falling back to form login', err);
      // Fallback to OAuth2 form login
      const params = new URLSearchParams();
      params.append('username', credentials.username);
      params.append('password', credentials.password);
      const resp = await fetch(`${(apiClient as any).baseURL || ''}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!resp.ok) {
        const text = await resp.text();
        console.error('Form login failed', resp.status, text);
        throw new Error(text || `Login failed (${resp.status})`);
      }
      let token: Token;
      try {
        token = await resp.json() as Token;
      } catch (jsonError) {
        console.error('Failed to parse login response', jsonError);
        throw jsonError;
      }
      apiClient.setAuthToken(token.access_token);
      return token;
    }
  }

  async register(data: RegisterRequest): Promise<Token> {
    const token = await apiClient.post<Token>('/auth/register', data);
    apiClient.setAuthToken(token.access_token);
    return token;
  }

  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/users/me');
  }

  logout(): void {
    apiClient.clearAuthToken();
  }

  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  getStoredToken(): string | null {
    return localStorage.getItem('auth_token');
  }
}

export const authService = new AuthService();
